/**
 * RemoteConfigurations Utility Functions
 *
 * Helper functions for filtering, sorting, and formatting configuration data.
 */

/**
 * Filter configurations based on search query and filter options
 * @param {Array} configurations - Array of configuration objects
 * @param {Object} filters - Filter state object
 * @param {string} filters.search - Search query string
 * @param {string} filters.environment - Environment filter value
 * @param {string} filters.status - Status filter value
 * @returns {Array} Filtered configurations
 */
var filterConfigurations = function (configurations, filters) {
  if (!configurations || !Array.isArray(configurations)) {
    return [];
  }

  return configurations.filter(function (config) {
    // Search filter - match name or description
    if (filters.search) {
      var searchLower = filters.search.toLowerCase();
      var nameMatch =
        config.configuration_name &&
        config.configuration_name.toLowerCase().indexOf(searchLower) !== -1;
      var descMatch =
        config.description &&
        config.description.toLowerCase().indexOf(searchLower) !== -1;
      if (!nameMatch && !descMatch) {
        return false;
      }
    }

    // Environment filter
    if (filters.environment && filters.environment !== 'all') {
      var hasEnv =
        config.environments &&
        config.environments.some(function (env) {
          return (
            env.toLowerCase().indexOf(filters.environment.toLowerCase()) !== -1
          );
        });
      if (!hasEnv) {
        return false;
      }
    }

    // Status filter
    if (filters.status && filters.status !== 'all') {
      var isEnabled =
        config.default_value === true || config.default_value === 'true';
      if (filters.status === 'enabled' && !isEnabled) {
        return false;
      }
      if (filters.status === 'disabled' && isEnabled) {
        return false;
      }
    }

    return true;
  });
};

/**
 * Count total targeting rules
 * @param {Object} config - Configuration object
 * @returns {number} Total count of targeting rules
 */
var countTargetingRules = function (config) {
  var count = 0;
  if (config.distributions && config.distributions.length > 0) {
    count += config.distributions.length;
  }
  if (config.farming_units && config.farming_units.length > 0) {
    count += config.farming_units.length;
  }
  if (config.users_email && config.users_email.length > 0) {
    count += config.users_email.length;
  }
  return count;
};

/**
 * Sort configurations by a given key and direction
 * @param {Array} configurations - Array of configuration objects
 * @param {Object} sortConfig - Sort configuration
 * @param {string} sortConfig.key - Key to sort by
 * @param {string} sortConfig.direction - 'asc' or 'desc'
 * @returns {Array} Sorted configurations
 */
var sortConfigurations = function (configurations, sortConfig) {
  if (!configurations || !Array.isArray(configurations)) {
    return [];
  }

  var sorted = configurations.slice();

  sorted.sort(function (a, b) {
    var aValue, bValue;

    switch (sortConfig.key) {
      case 'configuration_name':
        aValue = (a.configuration_name || '').toLowerCase();
        bValue = (b.configuration_name || '').toLowerCase();
        break;
      case 'default_value':
        // Sort by enabled/disabled (enabled first in asc)
        aValue = a.default_value === true || a.default_value === 'true' ? 1 : 0;
        bValue = b.default_value === true || b.default_value === 'true' ? 1 : 0;
        break;
      case 'environments':
        // Sort by number of environments
        aValue = a.environments ? a.environments.length : 0;
        bValue = b.environments ? b.environments.length : 0;
        break;
      case 'targeting':
        // Sort by total number of targeting rules
        aValue = countTargetingRules(a);
        bValue = countTargetingRules(b);
        break;
      case 'updated_at':
        aValue = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        bValue = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        break;
      default:
        aValue = a[sortConfig.key] || '';
        bValue = b[sortConfig.key] || '';
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  return sorted;
};

/**
 * Get a summary of targeting rules for display
 * @param {Object} config - Configuration object
 * @returns {string} Summary text like "3 rules" or "All"
 */
var getTargetingSummary = function (config) {
  if (!config) {
    return 'All';
  }

  var count = 0;

  if (config.distributions && config.distributions.length > 0) {
    count += config.distributions.length;
  }
  if (config.farming_units && config.farming_units.length > 0) {
    count += config.farming_units.length;
  }
  if (config.users_email && config.users_email.length > 0) {
    count += config.users_email.length;
  }

  if (count === 0) {
    return 'All';
  }

  return count + ' rule' + (count === 1 ? '' : 's');
};

/**
 * Format environment names for display (extract env name from "env :True" format)
 * @param {Array} environments - Array of environment strings
 * @returns {Array} Array of formatted environment names
 */
var formatEnvironments = function (environments) {
  if (!environments || !Array.isArray(environments)) {
    return [];
  }

  return environments
    .map(function (env) {
      // Handle format "stg :True" -> "stg"
      var parts = env.split(':');
      return parts[0].trim().toLowerCase();
    })
    .filter(function (env, index, self) {
      // Remove duplicates
      return self.indexOf(env) === index;
    });
};

/**
 * Get display label for environment
 * @param {string} envKey - Environment key (prod, stg, dev)
 * @returns {string} Display label
 */
var getEnvironmentLabel = function (envKey) {
  var labels = {
    prod: 'Production',
    stg: 'Staging',
    dev: 'Development',
  };
  return labels[envKey] || envKey;
};

/**
 * Format relative time from ISO timestamp
 * @param {string} timestamp - ISO timestamp string
 * @returns {string} Relative time string like "2h ago", "3d ago"
 */
var formatRelativeTime = function (timestamp) {
  if (!timestamp) {
    return '-';
  }

  var now = new Date();
  var then = new Date(timestamp);
  var diffMs = now - then;
  var diffSec = Math.floor(diffMs / 1000);
  var diffMin = Math.floor(diffSec / 60);
  var diffHour = Math.floor(diffMin / 60);
  var diffDay = Math.floor(diffHour / 24);

  if (diffDay > 30) {
    return then.toLocaleDateString();
  }
  if (diffDay > 0) {
    return diffDay + 'd ago';
  }
  if (diffHour > 0) {
    return diffHour + 'h ago';
  }
  if (diffMin > 0) {
    return diffMin + 'm ago';
  }
  return 'Just now';
};

/**
 * Prepare configuration data for API submission
 * @param {Object} config - Configuration form data
 * @returns {Object} Prepared configuration object
 */
var prepareConfigurationForSubmit = function (config) {
  var prepared = Object.assign({}, config);

  // Ensure default_value is boolean
  if (typeof prepared.default_value === 'string') {
    prepared.default_value = prepared.default_value.toLowerCase() === 'true';
  }

  // Handle users_email - convert string to array if needed
  if (typeof prepared.users_email === 'string') {
    if (prepared.users_email.length > 0) {
      prepared.users_email = prepared.users_email
        .split(',')
        .map(function (email) {
          return email.trim();
        })
        .filter(function (email) {
          // Basic email validation - must have content and @ symbol
          return email.length > 0 && email.indexOf('@') > 0;
        });
    } else {
      prepared.users_email = [];
    }
  }

  return prepared;
};

/**
 * Validate configuration form data
 * @param {Object} config - Configuration form data
 * @returns {Object} Validation result { isValid: boolean, errors: Object }
 */
var validateConfiguration = function (config) {
  var errors = {};

  if (!config.configuration_name || config.configuration_name.trim() === '') {
    errors.configuration_name = 'Feature name is required';
  }

  if (!config.description || config.description.trim() === '') {
    errors.description = 'Description is required';
  }

  // Validate extra_data JSON if provided
  if (config.extra_data && config.extra_data.trim() !== '') {
    try {
      JSON.parse(config.extra_data);
    } catch (e) {
      errors.extra_data = 'Extra data must be valid JSON';
    }
  }

  // Validate users_email if provided (can be string from form or array)
  if (config.users_email) {
    var emailsStr = '';
    if (typeof config.users_email === 'string') {
      emailsStr = config.users_email.trim();
    } else if (Array.isArray(config.users_email)) {
      emailsStr = config.users_email.join(',');
    }

    if (emailsStr !== '') {
      var emails = emailsStr.split(',').map(function (email) {
        return email.trim();
      });
      var invalidEmails = emails.filter(function (email) {
        // Basic email validation - must have content and @ symbol not at start
        return email.length > 0 && email.indexOf('@') <= 0;
      });
      if (invalidEmails.length > 0) {
        errors.users_email =
          'Invalid email format: ' + invalidEmails.join(', ');
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors: errors,
  };
};

module.exports = {
  filterConfigurations: filterConfigurations,
  sortConfigurations: sortConfigurations,
  getTargetingSummary: getTargetingSummary,
  formatEnvironments: formatEnvironments,
  getEnvironmentLabel: getEnvironmentLabel,
  formatRelativeTime: formatRelativeTime,
  prepareConfigurationForSubmit: prepareConfigurationForSubmit,
  validateConfiguration: validateConfiguration,
};
